<template>
  <div class="merchant-form-builder-page">
    <!-- Top Master Switch Card -->
    <b-card class="mb-2 shadow-sm border-0">
      <h4 class="font-weight-bolder mb-1 text-dark">
        Update Merchant Registration Settings
      </h4>
      <b-form-group
        id="group-form-builder-toggle"
        label="Form Builder"
        label-for="form-builder-toggle-group"
        label-class="font-weight-bold"
        description="Customize and manage the Merchant Registration form fields."
      >
        <b-form-radio-group
          id="form-builder-toggle-group"
          v-model="formBuilderEnabled"
          name="form-builder-toggle"
          :options="formBuilderOptions"
          value-field="item"
          text-field="name"
          disabled-field="notEnabled"
          class="d-flex align-items-center mt-50"
          @change="setMerchantRegistrationSettings()"
        />
      </b-form-group>
    </b-card>

    <!-- Main Workspace (Visible only when Form Builder is ON) -->
    <b-overlay
      :show="loading"
      rounded="sm"
      opacity="0.5"
      spinner-variant="primary"
    >
      <b-row
        v-if="formBuilderEnabled"
        class="match-height"
      >
        <!-- LEFT PANEL: Form Builder Settings -->
        <b-col
          :lg="showPreview ? 6 : 12"
          cols="12"
          class="transition-all"
        >
          <b-card
            no-body
            class="shadow-sm border-0 h-100 d-flex flex-column"
          >
            <!-- Card Header -->
            <div class="d-flex justify-content-between align-items-center p-2 border-bottom-light">
              <h4 class="font-weight-bolder m-0 text-dark">
                Form Builder
              </h4>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="btn-toggle-preview d-flex align-items-center"
                @click="showPreview = !showPreview"
              >
                <feather-icon
                  :icon="showPreview ? 'EyeOffIcon' : 'EyeIcon'"
                  class="mr-50"
                  size="14"
                />
                {{ showPreview ? 'Hide Preview' : 'Show Preview' }}
              </b-button>
            </div>

            <!-- Tab Navigation -->
            <div class="builder-tabs px-2 pt-1 border-bottom-light d-flex justify-content-between align-items-center">
              <div class="d-flex">
                <button
                  type="button"
                  class="tab-btn"
                  :class="{ active: activeTab === 'default' }"
                  @click="activeTab = 'default'"
                >
                  Default Fields
                </button>
                <button
                  type="button"
                  class="tab-btn ml-2"
                  :class="{ active: activeTab === 'custom' }"
                  @click="activeTab = 'custom'"
                >
                  Custom Fields
                </button>
              </div>

              <!-- Top Add Custom Field Button -->
              <b-button
                v-if="activeTab === 'custom' && customFields.length > 0"
                variant="primary"
                size="sm"
                class="d-flex align-items-center mb-1"
                @click="openAddCustomFieldModal"
              >
                <feather-icon
                  icon="PlusIcon"
                  class="mr-25"
                  size="14"
                />
                Add Custom Field
              </b-button>
            </div>

            <!-- Card Content Body -->
            <div class="card-body p-2 flex-grow-1 overflow-auto">
              <!-- ================= DEFAULT FIELDS TAB ================= -->
              <div v-if="activeTab === 'default'">
                <div
                  v-for="sectionName in activeDefaultSections"
                  :key="sectionName"
                  class="section-group mb-2"
                >
                  <h6 class="section-title font-weight-bolder text-dark mb-1">
                    {{ sectionName }}
                  </h6>

                  <div class="table-responsive rounded-table">
                    <table class="table field-table mb-0">
                      <thead>
                        <tr>
                          <th style="width: 40%;">
                            FIELD
                          </th>
                          <th
                            class="text-center"
                            style="width: 15%;"
                          >
                            VISIBLE
                          </th>
                          <th
                            class="text-center"
                            style="width: 15%;"
                          >
                            MANAGE TAG
                          </th>
                          <th
                            class="text-center"
                            style="width: 15%;"
                          >
                            REQUIRED
                          </th>
                          <th
                            class="text-center"
                            style="width: 15%;"
                          >
                            ACTIONS
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="field in getFieldsBySection(sectionName, 'default')"
                          :key="field.key"
                        >
                          <!-- Field Name & Monospace Key -->
                          <td>
                            <div class="field-label-text font-weight-bold">
                              {{ field.label }}
                            </div>
                            <div class="field-key-text text-muted">
                              {{ formatKeyDisplay(field.key) }}
                            </div>
                          </td>

                          <!-- Visible Toggle -->
                          <td class="text-center align-middle">
                            <div
                              v-b-tooltip.hover.v-primary="getVisibleTooltip(field)"
                              class="d-inline-block"
                            >
                              <b-form-checkbox
                                :id="'default-visible-' + field.key"
                                v-model="field.enabled"
                                switch
                                :disabled="Boolean(field.is_mandatory)"
                                class="custom-switch-primary d-inline-block"
                                :class="{ 'switch-mandatory-light': field.is_mandatory }"
                              >
                                <span class="sr-only">Toggle Visibility</span>
                              </b-form-checkbox>
                            </div>
                          </td>

                          <!-- Manage Tag Toggle -->
                          <td class="text-center align-middle">
                            <div
                              v-b-tooltip.hover.v-primary="getManageTagTooltip(field)"
                              class="d-inline-block"
                            >
                              <b-form-checkbox
                                :id="'default-tag-' + field.key"
                                v-model="field.is_tag"
                                switch
                                class="custom-switch-primary d-inline-block"
                              >
                                <span class="sr-only">Toggle Tag</span>
                              </b-form-checkbox>
                            </div>
                          </td>

                          <!-- Required Toggle -->
                          <td class="text-center align-middle">
                            <div
                              v-b-tooltip.hover.v-primary="getRequiredTooltip(field)"
                              class="d-inline-block"
                            >
                              <b-form-checkbox
                                :id="'default-required-' + field.key"
                                v-model="field.required"
                                switch
                                :disabled="Boolean(field.is_mandatory)"
                                class="custom-switch-primary d-inline-block"
                                :class="{ 'switch-mandatory-light': field.is_mandatory }"
                              >
                                <span class="sr-only">Toggle Required</span>
                              </b-form-checkbox>
                            </div>
                          </td>

                          <!-- Actions Menu -->
                          <td class="text-center align-middle">
                            <b-dropdown
                              variant="link"
                              toggle-class="text-muted p-0"
                              no-caret
                              right
                            >
                              <template #button-content>
                                <div class="action-btn-wrapper">
                                  <feather-icon
                                    icon="MoreVerticalIcon"
                                    size="16"
                                  />
                                </div>
                              </template>
                              <b-dropdown-item @click="openEditFieldModal(field)">
                                <feather-icon
                                  icon="EditIcon"
                                  class="mr-50"
                                  size="14"
                                />
                                Edit
                              </b-dropdown-item>
                            </b-dropdown>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- ================= CUSTOM FIELDS TAB ================= -->
              <div v-else-if="activeTab === 'custom'">
                <!-- Empty State -->
                <div
                  v-if="customFields.length === 0"
                  class="custom-fields-empty-state d-flex flex-column align-items-center justify-content-center py-4"
                >
                  <div class="empty-icon-circle mb-1 text-muted">
                    <feather-icon
                      icon="SlidersIcon"
                      size="32"
                    />
                  </div>
                  <h5 class="font-weight-bolder text-dark mb-50">
                    No custom fields yet
                  </h5>
                  <p class="text-muted font-small-3 mb-2 text-center">
                    Add fields to collect extra information from merchants.
                  </p>
                  <b-button
                    variant="primary"
                    class="d-flex align-items-center"
                    @click="openAddCustomFieldModal"
                  >
                    <feather-icon
                      icon="PlusIcon"
                      class="mr-50"
                      size="15"
                    />
                    Add Custom Field
                  </b-button>
                </div>

                <!-- Custom Fields Grouped by Section -->
                <div v-else>
                  <div
                    v-for="sectionName in customSections"
                    :key="sectionName"
                    class="section-group mb-2"
                  >
                    <h6 class="section-title font-weight-bolder text-dark mb-1">
                      {{ sectionName }}
                    </h6>

                    <div class="table-responsive rounded-table">
                      <table class="table field-table mb-0">
                        <thead>
                          <tr>
                            <th style="width: 40%;">
                              FIELD
                            </th>
                            <th
                              class="text-center"
                              style="width: 15%;"
                            >
                              VISIBLE
                            </th>
                            <th
                              class="text-center"
                              style="width: 15%;"
                            >
                              MANAGE TAG
                            </th>
                            <th
                              class="text-center"
                              style="width: 15%;"
                            >
                              REQUIRED
                            </th>
                            <th
                              class="text-center"
                              style="width: 15%;"
                            >
                              ACTIONS
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="field in getFieldsBySection(sectionName, 'custom')"
                            :key="field.key"
                          >
                            <td>
                              <div class="field-label-text font-weight-bold">
                                {{ field.label }}
                              </div>
                              <div class="field-key-text text-muted">
                                {{ formatKeyDisplay(field.key) }}
                              </div>
                            </td>
                            <!-- Visible Toggle -->
                            <td class="text-center align-middle">
                              <div
                                v-b-tooltip.hover.v-primary="getVisibleTooltip(field)"
                                class="d-inline-block"
                              >
                                <b-form-checkbox
                                  :id="'custom-visible-' + field.key"
                                  v-model="field.enabled"
                                  switch
                                  class="custom-switch-primary d-inline-block"
                                >
                                  <span class="sr-only">Toggle Visibility</span>
                                </b-form-checkbox>
                              </div>
                            </td>

                            <!-- Manage Tag Toggle -->
                            <td class="text-center align-middle">
                              <div
                                v-b-tooltip.hover.v-primary="getManageTagTooltip(field)"
                                class="d-inline-block"
                              >
                                <b-form-checkbox
                                  :id="'custom-tag-' + field.key"
                                  v-model="field.is_tag"
                                  switch
                                  class="custom-switch-primary d-inline-block"
                                >
                                  <span class="sr-only">Toggle Tag</span>
                                </b-form-checkbox>
                              </div>
                            </td>

                            <!-- Required Toggle -->
                            <td class="text-center align-middle">
                              <div
                                v-b-tooltip.hover.v-primary="getRequiredTooltip(field)"
                                class="d-inline-block"
                              >
                                <b-form-checkbox
                                  :id="'custom-required-' + field.key"
                                  v-model="field.required"
                                  switch
                                  class="custom-switch-primary d-inline-block"
                                >
                                  <span class="sr-only">Toggle Required</span>
                                </b-form-checkbox>
                              </div>
                            </td>
                            <td class="text-center align-middle">
                              <b-dropdown
                                variant="link"
                                toggle-class="text-muted p-0"
                                no-caret
                                right
                              >
                                <template #button-content>
                                  <div class="action-btn-wrapper">
                                    <feather-icon
                                      icon="MoreVerticalIcon"
                                      size="16"
                                    />
                                  </div>
                                </template>
                                <b-dropdown-item @click="openEditFieldModal(field)">
                                  <feather-icon
                                    icon="EditIcon"
                                    class="mr-50"
                                    size="14"
                                  />
                                  Edit
                                </b-dropdown-item>
                              </b-dropdown>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Builder Card Footer with Submit Button -->
            <div class="p-2 border-top-light d-flex justify-content-end">
              <b-button
                variant="primary"
                class="px-3"
                :disabled="saving"
                @click="saveConfiguration"
              >
                <b-spinner
                  v-if="saving"
                  small
                  class="mr-50"
                />
                Submit
              </b-button>
            </div>
          </b-card>
        </b-col>

        <!-- RIGHT PANEL: Live Preview -->
        <b-col
          v-if="showPreview"
          lg="6"
          cols="12"
        >
          <b-card
            no-body
            class="shadow-sm border-0 h-100 preview-card"
          >
            <div class="p-2 border-bottom-light">
              <h4 class="font-weight-bolder m-0 text-dark">
                Live Preview
              </h4>
              <small class="text-muted">
                Rearrange fields — Drag and drop fields to change their position.
              </small>
            </div>

            <div class="card-body p-2 overflow-auto">
              <div class="preview-form-box p-3 bg-white border rounded">
                <div class="text-center mb-3">
                  <h5 class="font-weight-bolder text-dark mb-25 letter-spacing-1">
                    NEW MERCHANT REGISTRATION
                  </h5>
                </div>

                <!-- Live Rendered Sections -->
                <div
                  v-for="section in allDisplaySections"
                  :key="section"
                  class="preview-section mb-3"
                >
                  <h6 class="preview-section-title font-weight-bolder text-center text-dark pb-50 mb-2 border-bottom">
                    {{ section }}
                  </h6>

                  <b-row :class="{ 'justify-content-center': section === 'Merchant Type' }">
                    <b-col
                      v-for="field in getPreviewFieldsBySection(section)"
                      :key="field.key"
                      :cols="12"
                      :md="getFieldColSpan(field)"
                      class="mb-1"
                    >
                      <b-form-group
                        :id="'group-preview-' + field.key"
                        :label-for="'preview-input-' + field.key"
                        class="mb-0"
                      >
                        <template #label>
                          <span
                            v-if="field.key !== 'merchant_type'"
                            class="font-weight-bold font-small-3 text-dark"
                          >
                            {{ field.label }}
                            <span
                              v-if="field.required"
                              class="text-danger"
                            >*</span>
                          </span>

                          <span
                            v-else
                            class="sr-only"
                          >
                            {{ field.label }}
                          </span>
                        </template>

                        <!-- Text Input -->
                        <b-form-input
                          v-if="field.type === 'text'"
                          :id="'preview-input-' + field.key"
                          :placeholder="field.placeholder || ''"
                          disabled
                        />

                        <!-- Number Input -->
                        <b-form-input
                          v-else-if="field.type === 'number'"
                          :id="'preview-input-' + field.key"
                          type="number"
                          :placeholder="field.placeholder || ''"
                          disabled
                        />

                        <!-- Textarea -->
                        <b-form-textarea
                          v-else-if="field.type === 'textarea'"
                          :id="'preview-input-' + field.key"
                          :placeholder="field.placeholder || ''"
                          rows="3"
                          disabled
                        />

                        <!-- Select -->
                        <b-form-select
                          v-else-if="field.type === 'select'"
                          :id="'preview-input-' + field.key"
                          disabled
                          :options="getSelectFieldOptions(field)"
                        />

                        <!-- Radio Group -->
                        <div
                          v-else-if="field.type === 'radio'"
                          class="d-flex align-items-center"
                          :class="{ 'justify-content-center': field.key === 'merchant_type' }"
                        >
                          <b-form-radio-group
                            :id="'preview-input-' + field.key"
                            :checked="getRadioDefaultValue(field)"
                            :options="getRadioFieldOptions(field)"
                            class="mt-1"
                            disabled
                          />
                        </div>

                        <!-- File Input -->
                        <b-form-file
                          v-else-if="field.type === 'file'"
                          :id="'preview-input-' + field.key"
                          :placeholder="field.placeholder || 'Choose a file...'"
                          disabled
                        />

                        <!-- Checkbox -->
                        <b-form-checkbox
                          v-else-if="field.type === 'checkbox'"
                          :id="'preview-input-' + field.key"
                          disabled
                        >
                          {{ field.placeholder || field.label }}
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>

                <!-- Preview Submit Buttons -->
                <div class="d-flex justify-content-end mt-2 pt-1 border-top">
                  <b-button
                    variant="primary"
                    size="sm"
                    class="mr-1 px-2"
                    disabled
                  >
                    Submit
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    class="px-2"
                    disabled
                  >
                    Clear
                  </b-button>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>

    <!-- ================= ADD CUSTOM FIELD MODAL ================= -->
    <b-modal
      id="modal-add-custom-field"
      ref="addCustomFieldModal"
      hide-footer
      centered
      no-close-on-backdrop
      title="ADD CUSTOM FIELD"
    >
      <div class="custom-modal-content">
        <b-form @submit.prevent="submitAddCustomField">
          <!-- Field Label -->
          <b-form-group
            id="group-custom-field-label"
            label-for="custom-field-label"
          >
            <template #label>
              <span class="font-weight-bold">Field Label <span class="text-danger">*</span></span>
            </template>
            <b-form-input
              id="custom-field-label"
              v-model="customForm.label"
              placeholder="e.g. Tax Identification No."
              required
            />
          </b-form-group>

          <!-- Placeholder -->
          <b-form-group
            id="group-custom-field-placeholder"
            label="Placeholder Text"
            label-for="custom-field-placeholder"
            label-class="font-weight-bold"
          >
            <b-form-input
              id="custom-field-placeholder"
              v-model="customForm.placeholder"
              placeholder="e.g. Enter the TIN"
            />
          </b-form-group>

          <!-- Type and Section in 2 columns -->
          <b-row>
            <b-col md="6">
              <b-form-group
                id="group-custom-field-type"
                label-for="custom-field-type"
              >
                <template #label>
                  <span class="font-weight-bold">Field Type <span class="text-danger">*</span></span>
                </template>
                <b-form-select
                  id="custom-field-type"
                  v-model="customForm.type"
                  :options="fieldTypeOptions"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                id="group-custom-field-section"
                label-for="custom-field-section"
              >
                <template #label>
                  <span class="font-weight-bold">Section <span class="text-danger">*</span></span>
                </template>
                <b-form-select
                  id="custom-field-section"
                  v-model="customForm.section"
                  :options="sectionDropdownOptions"
                  required
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Dynamic Options (If Field Type is Select or Radio) -->
          <b-form-group
            v-if="customForm.type === 'select' || customForm.type === 'radio'"
            id="group-custom-field-options"
            label-for="custom-field-options"
            description="Enter options separated by commas."
          >
            <template #label>
              <span class="font-weight-bold">Options (Comma separated) <span class="text-danger">*</span></span>
            </template>
            <b-form-input
              id="custom-field-options"
              v-model="customForm.raw_options"
              placeholder="e.g. Cash, Credit"
              required
            />
          </b-form-group>

          <!-- Field Width -->
          <b-form-group
            id="group-custom-field-width"
            label="Field Width"
            label-for="custom-field-width-radio"
            label-class="font-weight-bold d-block"
            class="mb-2"
          >
            <b-form-radio-group
              id="custom-field-width-radio"
              v-model="customForm.width"
              class="d-flex"
            >
              <b-form-radio
                value="half"
                class="mr-3"
              >
                Half Width
              </b-form-radio>
              <b-form-radio value="full">
                Full Width
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>

          <!-- Required and Manage Tag switches -->
          <b-row class="mb-2">
            <b-col cols="6">
              <b-form-group
                id="group-custom-required"
                label="Required/Optional"
                label-for="custom-required-switch"
                label-class="font-weight-bold d-block"
                class="mb-0"
              >
                <div class="d-flex align-items-center mt-50">
                  <b-form-checkbox
                    id="custom-required-switch"
                    v-model="customForm.required"
                    switch
                    class="custom-switch-primary mr-1"
                  >
                    <span class="font-small-3">{{ customForm.required ? 'Required' : 'Optional' }}</span>
                  </b-form-checkbox>
                </div>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                id="group-custom-tag"
                label="Manage Tag"
                label-for="custom-tag-switch"
                label-class="font-weight-bold d-block"
                class="mb-0"
              >
                <div class="d-flex align-items-center mt-50">
                  <b-form-checkbox
                    id="custom-tag-switch"
                    v-model="customForm.is_tag"
                    switch
                    class="custom-switch-primary mr-1"
                  >
                    <span class="font-small-3">{{ customForm.is_tag ? 'On' : 'Off' }}</span>
                  </b-form-checkbox>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Modal Actions -->
          <div class="d-flex justify-content-end align-items-center pt-1">
            <b-button
              type="submit"
              variant="primary"
              class="mr-1 px-3"
            >
              Submit
            </b-button>
            <b-button
              type="button"
              variant="outline-secondary"
              class="px-3"
              @click="resetCustomFieldForm"
            >
              Clear
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>

    <!-- ================= EDIT FIELD MODAL ================= -->
    <b-modal
      id="modal-edit-field"
      ref="editFieldModal"
      hide-footer
      centered
      no-close-on-backdrop
      title="EDIT FIELD"
    >
      <div class="custom-modal-content">
        <b-form @submit.prevent="submitEditField">
          <!-- Field Label -->
          <b-form-group
            id="group-edit-field-label"
            label-for="edit-field-label"
          >
            <template #label>
              <span class="font-weight-bold">Field Label <span class="text-danger">*</span></span>
            </template>
            <b-form-input
              id="edit-field-label"
              v-model="editForm.label"
              placeholder="Enter field label"
              required
            />
          </b-form-group>

          <!-- Placeholder -->
          <b-form-group
            id="group-edit-field-placeholder"
            label="Placeholder Text"
            label-for="edit-field-placeholder"
            label-class="font-weight-bold"
          >
            <b-form-input
              id="edit-field-placeholder"
              v-model="editForm.placeholder"
              placeholder="Enter placeholder text"
            />
          </b-form-group>

          <!-- Field Width -->
          <b-form-group
            id="group-edit-field-width"
            label="Field Width"
            label-for="edit-field-width-radio"
            label-class="font-weight-bold d-block"
            class="mb-2"
          >
            <b-form-radio-group
              id="edit-field-width-radio"
              v-model="editForm.width"
              class="d-flex"
            >
              <b-form-radio
                value="half"
                class="mr-3"
              >
                Half Width
              </b-form-radio>
              <b-form-radio value="full">
                Full Width
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>

          <!-- Modal Actions -->
          <div class="d-flex justify-content-end align-items-center pt-1">
            <b-button
              type="submit"
              variant="primary"
              class="mr-1 px-3"
            >
              Submit
            </b-button>
            <b-button
              type="button"
              variant="outline-secondary"
              class="px-3"
              @click="resetEditForm"
            >
              Clear
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BFormFile,
  BFormCheckbox,
  BFormRadio,
  BFormRadioGroup,
  BModal,
  BDropdown,
  BDropdownItem,
  BOverlay,
  BSpinner,
  BForm,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const MerchantRegistrationBuilderRepository = RepositoryFactory.get('merchantRegistrationBuilder')

export default {
  name: 'MerchantRegistrationFormBuilder',
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormFile,
    BFormCheckbox,
    BFormRadio,
    BFormRadioGroup,
    BModal,
    BDropdown,
    BDropdownItem,
    BOverlay,
    BSpinner,
    BForm,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      loading: false,
      saving: false,
      formBuilderEnabled: false,
      showPreview: true,
      activeTab: 'default',
      version: 1,
      sections: [],
      fields: [],
      defaultSections: [
        'Merchant Primary Business Details',
        'Merchant Admin Details',
        'Bank Details',
        'Merchant Type',
      ],
      sectionDropdownOptions: [
        { value: 'Merchant Primary Business Details', text: 'Merchant Primary Business Details' },
        { value: 'Merchant Admin Details', text: 'Merchant Admin Details' },
        { value: 'Bank Details', text: 'Bank Details' },
        { value: 'Additional Details', text: 'Additional Details' },
      ],
      fieldTypeOptions: [
        { value: 'text', text: 'Text Input' },
        { value: 'number', text: 'Number' },
        { value: 'select', text: 'Dropdown / Select' },
        { value: 'radio', text: 'Radio Buttons' },
        { value: 'checkbox', text: 'Checkbox' },
        { value: 'textarea', text: 'Textarea' },
        { value: 'file', text: 'File Upload' },
      ],
      customForm: {
        label: '',
        placeholder: '',
        type: 'text',
        section: 'Additional Details',
        width: 'half',
        required: false,
        is_tag: false,
        raw_options: '',
      },
      editingField: null,
      editForm: {
        label: '',
        placeholder: '',
        width: 'half',
      },
      fieldToDelete: null,
      formBuilderOptions: [
        { item: true, name: 'On' },
        { item: false, name: 'Off' },
      ],
    }
  },
  computed: {
    activeDefaultSections() {
      return this.defaultSections.filter(sectionName => this.fields.some(f => {
        const { section, group } = f
        return section === sectionName && group === 'default'
      }))
    },

    customFields() {
      return this.fields.filter(f => f.group === 'custom')
    },

    customSections() {
      const sections = []
      this.customFields.forEach(field => {
        const { section } = field
        const sec = section || 'Additional Details'
        if (!sections.includes(sec)) {
          sections.push(sec)
        }
      })
      if (sections.length > 0) {
        return sections
      }
      return ['Additional Details']
    },

    allDisplaySections() {
      const activeSections = []
      this.fields
        .filter(f => f.enabled)
        .forEach(f => {
          const { section } = f
          const sec = section || 'Additional Details'
          if (!activeSections.includes(sec)) {
            activeSections.push(sec)
          }
        })
      return activeSections
    },
  },
  async created() {
    await this.readMerchantRegistrationSettings()
    await this.fetchConfiguration()
  },
  methods: {
    async readMerchantRegistrationSettings() {
      this.loading = true
      try {
        const { data } = (await MerchantRegistrationBuilderRepository.getMerchantRegistrationSettings()).data
        this.formBuilderEnabled = data.form_builder_enabled
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },

    async setMerchantRegistrationSettings() {
      this.loading = true
      try {
        const payload = {
          form_builder_enabled: this.formBuilderEnabled,
        }
        const res = await MerchantRegistrationBuilderRepository.updateMerchantRegistrationSettings(payload)
        if (res.status === 200) {
          this.showSuccessMessage('Merchant Registration Settings Updated Successfully')
        } else {
          this.showErrorMessage('Cannot Update Merchant Registration Settings')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      await this.readMerchantRegistrationSettings()
      await this.fetchConfiguration()
      this.loading = false
    },

    getVisibleTooltip(field) {
      return field.enabled ? 'Field is visible' : 'Field is hidden'
    },

    getManageTagTooltip(field) {
      return field.is_tag ? 'Used as an agreement tag' : 'Not used as an agreement tag'
    },

    getRequiredTooltip(field) {
      return field.required ? 'Field is required' : 'Field is optional'
    },

    formatKeyDisplay(key) {
      return `{{${key}}}`
    },

    getFieldColSpan(field) {
      const { width, type } = field
      if (width === 'full' || type === 'textarea') {
        return 12
      }
      return 6
    },

    getFieldWidth(field) {
      const { width, type } = field
      if (width) {
        return width
      }
      if (type === 'textarea') {
        return 'full'
      }
      return 'half'
    },

    normalizeSectionName(section) {
      if (!section) {
        return 'Merchant Primary Business Details'
      }
      if (section === 'Business Details') {
        return 'Merchant Primary Business Details'
      }
      if (section === 'Admin Details') {
        return 'Merchant Admin Details'
      }
      return section
    },

    mapSectionForBackend(section) {
      if (section === 'Merchant Primary Business Details') {
        return 'Business Details'
      }
      if (section === 'Merchant Admin Details') {
        return 'Admin Details'
      }
      return section
    },

    async fetchConfiguration() {
      this.loading = true
      try {
        const response = await MerchantRegistrationBuilderRepository.getConfiguration()
        const { data: responseBody } = response
        const configData = responseBody.data ? responseBody.data : responseBody

        const { version, sections, fields } = configData

        this.version = version || 1
        this.sections = sections || []

        const fetchedFields = fields || []
        this.fields = fetchedFields.map(f => {
          const {
            section, options, enabled, required, is_tag,
          } = f
          return {
            ...f,
            section: this.normalizeSectionName(section),
            width: this.getFieldWidth(f),
            enabled: Boolean(enabled),
            required: Boolean(required),
            is_tag: Boolean(is_tag),
            options: Array.isArray(options) ? options : [],
          }
        })
      } catch (err) {
        this.convertAndNotifyError(err)
      } finally {
        this.loading = false
      }
    },

    getFieldsBySection(sectionName, group) {
      return this.fields.filter(f => {
        const { section, group: fieldGroup } = f
        const matchesSection = section === sectionName
        const matchesGroup = fieldGroup === group
        return matchesSection && matchesGroup
      })
    },

    getPreviewFieldsBySection(sectionName) {
      return this.fields
        .filter(f => {
          const { section, enabled } = f
          return section === sectionName && enabled
        })
        .sort((a, b) => {
          const { order: orderA = 0 } = a
          const { order: orderB = 0 } = b
          return orderA - orderB
        })
    },

    getSelectFieldOptions(field) {
      const { placeholder, options } = field
      const defaultPlaceholder = placeholder || 'Select an option'
      const opts = [{ value: null, text: defaultPlaceholder, disabled: true }]
      if (Array.isArray(options) && options.length > 0) {
        options.forEach(opt => {
          opts.push({ value: opt, text: opt })
        })
      }
      return opts
    },

    getRadioFieldOptions(field) {
      const { options } = field
      if (Array.isArray(options) && options.length > 0) {
        return options.map(opt => ({ text: opt, value: opt.toLowerCase() }))
      }
      return []
    },

    getRadioDefaultValue(field) {
      const { options } = field
      if (Array.isArray(options) && options.length > 0) {
        const defaultChoice = options[1] || options[0]
        return defaultChoice.toLowerCase()
      }
      return null
    },

    // ================= ADD CUSTOM FIELD =================
    openAddCustomFieldModal() {
      this.resetCustomFieldForm()
      this.$refs.addCustomFieldModal.show()
    },

    resetCustomFieldForm() {
      this.customForm = {
        label: '',
        placeholder: '',
        type: 'text',
        section: 'Additional Details',
        width: 'half',
        required: false,
        is_tag: true,
        raw_options: '',
      }
    },

    async submitAddCustomField() {
      const {
        label,
        placeholder,
        type,
        section,
        width,
        required,
        is_tag,
        raw_options,
      } = this.customForm

      if (!label.trim()) return

      let generatedKey = label
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '_')
        .replace(/^_+|_+$/g, '')

      if (!generatedKey || !/^[a-z]/.test(generatedKey)) {
        generatedKey = `custom_${generatedKey}`
      }

      const existingKeys = new Set(this.fields.map(f => f.key))
      let finalKey = generatedKey
      let counter = 1
      while (existingKeys.has(finalKey)) {
        finalKey = `${generatedKey}_${counter}`
        counter += 1
      }

      let options = []
      if (type === 'select' || type === 'radio') {
        options = raw_options
          .split(',')
          .map(o => o.trim())
          .filter(Boolean)

        if (options.length === 0) {
          this.showErrorMessage(`At least one option is required for ${type} field.`)
          return
        }
      }

      const newField = {
        key: finalKey,
        label: label.trim(),
        placeholder: placeholder ? placeholder.trim() : '',
        type,
        section,
        group: 'custom',
        width,
        required: Boolean(required),
        enabled: true,
        is_tag: Boolean(is_tag),
        order: this.fields.length + 1,
        options,
      }

      this.fields.push(newField)
      this.$refs.addCustomFieldModal.hide()

      await this.saveConfiguration()
    },

    // ================= EDIT FIELD =================
    openEditFieldModal(field) {
      this.editingField = field
      const { label, placeholder, width } = field
      this.editForm = {
        label,
        placeholder: placeholder || '',
        width: width || 'half',
      }
      this.$refs.editFieldModal.show()
    },

    resetEditForm() {
      if (this.editingField) {
        const { label, placeholder, width } = this.editingField
        this.editForm = {
          label,
          placeholder: placeholder || '',
          width: width || 'half',
        }
      }
    },

    async submitEditField() {
      const { label, placeholder, width } = this.editForm
      if (!this.editingField || !label.trim()) return

      this.editingField.label = label.trim()
      this.editingField.placeholder = placeholder.trim()
      this.editingField.width = width

      this.$refs.editFieldModal.hide()
      this.editingField = null

      await this.saveConfiguration()
    },

    // ================= SAVE CONFIGURATION =================
    async saveConfiguration() {
      this.saving = true
      try {
        const sanitizedFields = this.fields.map((f, index) => {
          const {
            key,
            section,
            label,
            placeholder,
            type,
            required,
            enabled,
            is_tag,
            group,
            options,
            width,
            is_mandatory,
          } = f

          const isMandatory = Boolean(is_mandatory)

          return {
            key,
            section: this.mapSectionForBackend(section),
            label,
            placeholder: placeholder || null,
            type,
            required: isMandatory ? true : Boolean(required),
            enabled: isMandatory ? true : Boolean(enabled),
            is_mandatory: isMandatory,
            is_tag: Boolean(is_tag),
            order: index + 1,
            group: group || 'default',
            options: Array.isArray(options) ? options : [],
            width: width || 'half',
          }
        })

        const { version, sections } = this
        const payload = {
          configuration: {
            version: version || 1,
            sections: sections.length > 0 ? sections : undefined,
            fields: sanitizedFields,
          },
        }

        const res = await MerchantRegistrationBuilderRepository.saveConfiguration(payload)
        const { data } = res
        this.showSuccessMessage(data.message || 'Form configuration saved successfully.')
      } catch (err) {
        this.convertAndNotifyError(err)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../@core/scss/vue/libs/vue-select.scss';
.merchant-form-builder-page {
  font-family: inherit;

  .border-bottom-light {
    border-bottom: 1px solid #ebe9f1;
  }
  .border-top-light {
    border-top: 1px solid #ebe9f1;
  }

  .btn-toggle-preview {
    font-size: 0.85rem;
    font-weight: 500;
    border-color: #d8d6de;
    color: #5e5873;
    &:hover {
      background-color: #f8f8f8;
      color: #333;
    }
  }

  .builder-tabs {
    .tab-btn {
      background: none;
      border: none;
      font-size: 0.95rem;
      font-weight: 600;
      color: #6e6b7b;
      padding: 0.5rem 0.25rem 0.75rem 0.25rem;
      position: relative;
      cursor: pointer;
      outline: none;

      &.active {
        color: var(--primary);
        &::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 2px;
          background-color: var(--primary);
        }
      }
    }
  }

  .section-title {
    font-size: 0.88rem;
    letter-spacing: 0.3px;
  }

  .rounded-table {
    border: 1px solid #f0f2f5;
    border-radius: 6px;
    background: #ffffff;
  }

  .field-table {
    thead th {
      background-color: #ECF6FF !important;
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.5px;
      border: none;
      padding: 0.75rem 1rem;
    }

    tbody td {
      border-top: 1px solid #ECF6FF !important;
      padding: 0.75rem 1rem;
      vertical-align: middle;
    }

    .field-label-text {
      font-size: 0.85rem;
      color: #333333;
    }

    .field-key-text {
      font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
      font-size: 0.75rem;
      color: #82868b;
    }

    .action-btn-wrapper {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background: #fff;
      cursor: pointer;
      &:hover {
        background-color: #f8f8f8;
      }
    }
  }

  ::v-deep .custom-switch-primary .custom-control-input:checked ~ .custom-control-label::before {
    background-color: var(--primary) !important;
    border-color: var(--primary) !important;
  }

  ::v-deep .switch-mandatory-light {
    .custom-control-input ~ .custom-control-label::before {
      opacity: 0.35 !important;
    }
  }

  .custom-fields-empty-state {
    border: 2px dashed #d8d6de;
    border-radius: 8px;
    background-color: #fafafc;
    min-height: 220px;

    .empty-icon-circle {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: #ececf3;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .preview-card {
    background-color: #f8f8fb;
    border: 1px solid #ebe9f1;
  }

  .preview-form-box {
    box-shadow: 0 4px 12px 0 rgba(34, 41, 47, 0.05);

    .preview-section-title {
      font-size: 0.85rem;
      letter-spacing: 0.5px;
      color: #333;
    }
  }

  .custom-modal-content {
    h5 {
      letter-spacing: 0.5px;
    }
    .close {
      color: #6e6b7b;
      opacity: 0.8;
      &:hover {
        opacity: 1;
      }
    }
  }
}
</style>
